import { ICpEntity } from '@se/cp.engine';
import { Parser, SUPPORTED_FORMULAS } from '@ebit/hot-formula-parser';
import { isNotEmpty } from '../export-clinical-eval/supports';
import {
    ApplyFilterParams,
    ApplyFindIndexParams,
    ApplyFindParams,
    ApplyMapParams,
    ApplyPivotFilterParams,
    ApplyPivotMultiFilterParams,
} from './interfaces';
import { Guid } from '@lib/tools/guid';
import { EArrayFuncion } from '@se/cp.common';

export const applyMap = (sourceArray: Array<any>, propToMap: string, fnName: string | null) => {
    const parser = new Parser();
    if (!Array.isArray(sourceArray)) return null;
    if (!!fnName && !SUPPORTED_FORMULAS.find((formula: string) => formula === fnName)) return null;
    if (!fnName) return sourceArray.map((entry) => entry[propToMap]);
    return parser._callFunction(fnName, propToMap ? sourceArray.map((entry) => entry[propToMap]) : sourceArray);
};

export const applyFind = (sourceArray: Array<any>, propToFind: string, matchValue: unknown, propToGet: string) => {
    if (!Array.isArray(sourceArray)) return null;
    const found = sourceArray.find((item) => item?.[propToFind] === matchValue);
    return (found && propToGet ? found[propToGet] : found) ?? null;
};

export const applyFindIndex = (sourceArray: Array<any>, propToFind: string, matchValue: unknown): number => {
    if (!Array.isArray(sourceArray)) return -1;
    const foundIndex = sourceArray.findIndex((item) => item?.[propToFind] === matchValue);
    return foundIndex ?? -1;
};

export const innerDeepGetProp = (obj: any, propList: string[]): any => {
    if (obj === null || typeof obj !== 'object') return null; // Aggiunta questa condizione per gestire gli input non validi
    if (!Array.isArray(propList)) return null;
    return propList.reduce((prev, curr) => {
        if (typeof prev !== 'object' || !prev) return null;
        return prev[curr];
    }, obj);
};

/**
 * la funzione cerca un oggetto all'interno dell'array,trova una proprietà specificata all'interno dell'oggetto
 * e restituire il suo valore corrispondente o l'oggetto in se  altrimenti restituisce null.
 * @param sourceArray
 * @param bindingToFind x.y.z
 * @param matchValue
 * @param propToGet se non specificata ritorna l'oggetto trovato
 */
export const applyFindDeep = (
    sourceArray: Array<any>,
    bindingToFind: string,
    matchValue: unknown,
    propToGet: string,
) => {
    if (!Array.isArray(sourceArray)) return null;
    const propsList = bindingToFind.split('.');
    const found = sourceArray.find((item) => innerDeepGetProp(item, propsList) === matchValue);
    return (found && propToGet ? found[propToGet] : found) ?? null;
};

/**
 * funzione simile alla precedente applyFindDeep,
 * ma anziché restituire l'oggetto trovato o una sua proprietà, restituisce l'indice dell'oggetto trovato nell'array.
 * @param sourceArray
 * @param bindingToFind
 * @param matchValue
 */
export const applyFindIndexDeep = (sourceArray: Array<any>, bindingToFind: string, matchValue: unknown): number => {
    if (!Array.isArray(sourceArray)) return -1;
    const propsList = bindingToFind.split('.');
    return sourceArray.findIndex((item) => innerDeepGetProp(item, propsList) === matchValue);
};

/**
 * funzione che applica un filtro a un array di oggetti. basandosi su una prop interna ad ogni oggetto ed un valore di confronto
 * @param sourceArray
 * @param propToCompare
 * @param matchValue
 */
export const applyFilter = (sourceArray: Array<any>, propToCompare: string, matchValue: unknown) => {
    if (!Array.isArray(sourceArray)) return [];
    return sourceArray.filter((item) => item[propToCompare] === matchValue);
};

/**
 * Filtra l'array di origine basandosi su un array pivot e chiavi esterne specificate.
 *
 * Questa funzione prende un array di origine e un array pivot di origine, e filtra l'array di origine
 * per restituire solo quegli elementi il cui `Id` è incluso nelle chiavi esterne filtrate ottenute
 * dall'array pivot di origine. Il filtraggio nell'array pivot di origine viene effettuato in base
 * al `fkIn` fornito e al `matchValue`, e le chiavi esterne risultanti sono selezionate usando `fkOut`.
 *
 * @param {Array<any>} sourceArray - L'array di oggetti da filtrare.
 * @param {Array<any>} pivotSourceArray - L'array su cui fare pivot per determinare quali elementi includere dall'array di origine.
 * @param {string} fkIn - La chiave in pivotSourceArray da confrontare con `matchValue`.
 * @param {string} fkOut - La chiave in pivotSourceArray i cui valori saranno usati per filtrare l'`Id` dell'array di origine.
 * @param {unknown} matchValue - Il valore da confrontare con `fkIn` nell'array pivot.
 * @returns {Array<any>} L'array filtrato contenente elementi dall'array di origine con chiavi esterne corrispondenti.
 */
export const applyPivotFilter = (
    sourceArray: Array<any>,
    pivotSourceArray: Array<any>,
    fkIn: string,
    fkOut: string,
    matchValue: unknown,
): Array<any> => {
    if (!Array.isArray(sourceArray)) return [];
    const foreignKeys = pivotSourceArray
        .filter((item) => item[fkIn] === matchValue)
        .map((item) => item[fkOut])
        .filter(isNotEmpty);
    const foreignKeysSet = new Set(foreignKeys);
    return sourceArray.filter((item) => foreignKeysSet.has(item.Id));
};

/**
 * Filtra l'array di origine basandosi su un array pivot e una lista di entità di confronto.
 *
 * Questa funzione prende un array di origine e un array pivot di origine, e filtra l'array di origine
 * per restituire solo quegli elementi il cui `Id` è incluso nelle chiavi esterne filtrate ottenute
 * dall'array pivot di origine. Il filtraggio nell'array pivot di origine viene effettuato in base
 * al `fkIn` fornito e agli `ids` ottenuti dalle entità di confronto, e le chiavi esterne risultanti
 * sono selezionate usando `fkOut`.
 *
 * @param {Array<any>} sourceArray - L'array di oggetti da filtrare.
 * @param {Array<any>} pivotSourceArray - L'array su cui fare pivot per determinare quali elementi includere dall'array di origine.
 * @param {string} fkIn - La chiave in pivotSourceArray da confrontare con gli `ids` ottenuti dalle entità di confronto.
 * @param {string} fkOut - La chiave in pivotSourceArray i cui valori saranno usati per filtrare l'`Id` dell'array di origine.
 * @param {ICpEntity[]} matchEntities - Le entità di confronto da cui ottenere gli `ids` per il filtraggio.
 * @param {string} propertyPath - Il percorso della proprietà da cui ottenere gli `ids` dalle entità di confronto.
 * @returns {Array<any>} L'array filtrato contenente elementi dall'array di origine con chiavi esterne corrispondenti.
 */
export const applyPivotMultiFilter = (
    sourceArray: Array<any>,
    pivotSourceArray: Array<any>,
    fkIn: string,
    fkOut: string,
    matchEntities: ICpEntity[],
    propertyPath: string,
): Array<any> => {
    if (!Array.isArray(sourceArray)) return [];
    const ids = matchEntities.map((entity) => entity.getNestedPropertyValue(propertyPath));
    const foreignKeys = pivotSourceArray
        .filter((item) => ids.includes(item[fkIn]))
        .map((item) => item[fkOut])
        .filter(isNotEmpty);
    const foreignKeysSet = new Set(foreignKeys);
    return sourceArray.filter((item) => foreignKeysSet.has(item.Id));
};

/**
 * clona l'array di origine creando nuovi id per le entity al suo interno
 * @param sourceArray
 */
export const arrayCloneDeep = (sourceArray: Array<any>): Array<any> => {
    if (!Array.isArray(sourceArray)) return [];
    return sourceArray.map((item) => {
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
            const Id = new Guid().toString();
            return { ...item, Id };
        }
        return item;
    });
};

export const arrayJoin = (sourceArray: Array<any>, props: string, separator: string): string => {
    if (!Array.isArray(sourceArray)) return '';
    const propsList = props.split('.');
    return sourceArray.map((item) => innerDeepGetProp(item, propsList)).join(separator);
};

export const arrayFunctionsMap: Record<EArrayFuncion, (...args: any[]) => any> = {
    APPLY_MAP: (params: ApplyMapParams) => applyMap(...params),
    APPLY_FIND: (params: ApplyFindParams) => applyFind(...params),
    APPLY_FIND_INDEX: (params: ApplyFindIndexParams) => applyFindIndex(...params),
    APPLY_FIND_DEEP: (params: ApplyFindParams) => applyFindDeep(...params),
    APPLY_FIND_INDEX_DEEP: (params: ApplyFindIndexParams) => applyFindIndexDeep(...params),
    APPLY_FILTER: (params: ApplyFilterParams) => applyFilter(...params),
    APPLY_PIVOT_FILTER: (params: ApplyPivotFilterParams) => applyPivotFilter(...params),
    APPLY_PIVOT_MULTI_FILTER: (params: ApplyPivotMultiFilterParams) => applyPivotMultiFilter(...params),
    ARRAY_CLONE_DEEP: (params: [arraySource: Array<any>]) => arrayCloneDeep(...params),
    ARRAY_JOIN: (params: [arraySource: Array<any>, prop: string, separator: string]) => arrayJoin(...params),
};
